import { useState } from 'react'
import { usePredictions } from '../hooks/usePredictions'
import { useAuth } from '../hooks/useAuth'
import AuthModal from '../components/AuthModal'

export default function MyStats() {
  const { user } = useAuth()
  const { predictions, getStats } = usePredictions()
  const [showAuth, setShowAuth] = useState(false)
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')

  const scored = predictions.filter(p => p.is_correct != null && p.actual_change != null)
  const pending = predictions.filter(p => p.is_correct == null)

  const correct = scored.filter(p => p.direction_correct).length
  const incorrect = scored.filter(p => !p.direction_correct).length
  const accuracy = scored.length > 0 ? Math.round((correct / scored.length) * 100) : 0
  const avgMoveCorrect = correct > 0
    ? (scored.filter(p => p.direction_correct).reduce((s, p) => s + Math.abs(parseFloat(p.actual_change) || 0), 0) / correct).toFixed(1)
    : '0.0'
  const avgMoveIncorrect = incorrect > 0
    ? (scored.filter(p => !p.direction_correct).reduce((s, p) => s + Math.abs(parseFloat(p.actual_change) || 0), 0) / incorrect).toFixed(1)
    : '0.0'
  const hypoReturn = scored.reduce((sum, p) =>
    sum + (p.direction_correct ? 10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100 : -10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100), 0)

  const fmtPnl = (val) => {
    const abs = Math.abs(val)
    const fmt = abs >= 1000 ? `$${(abs / 1000).toFixed(1)}k` : `$${abs.toFixed(0)}`
    return val >= 0 ? `+${fmt}` : `-${fmt}`
  }

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const sortedScored = [...scored].sort((a, b) => {
    if (sortKey === 'date') return sortDir === 'desc'
      ? new Date(b.report_date) - new Date(a.report_date)
      : new Date(a.report_date) - new Date(b.report_date)
    if (sortKey === 'move') return sortDir === 'desc'
      ? Math.abs(parseFloat(b.actual_change) || 0) - Math.abs(parseFloat(a.actual_change) || 0)
      : Math.abs(parseFloat(a.actual_change) || 0) - Math.abs(parseFloat(b.actual_change) || 0)
    if (sortKey === 'pnl') {
      const aPnl = (a.direction_correct ? 1 : -1) * Math.abs(parseFloat(a.actual_change) || 0)
      const bPnl = (b.direction_correct ? 1 : -1) * Math.abs(parseFloat(b.actual_change) || 0)
      return sortDir === 'desc' ? bPnl - aPnl : aPnl - bPnl
    }
    return 0
  })

  const SortArrow = ({ col }) => sortKey !== col
    ? <span style={{ color: 'var(--border2)', marginLeft: 4 }}>⇅</span>
    : <span style={{ color: 'var(--olive)', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>

  const statItems = [
    { label: 'Total Guesses', value: scored.length, color: 'var(--text)' },
    { label: 'Correct', value: correct, color: 'var(--green)' },
    { label: 'Incorrect', value: incorrect, color: 'var(--red)' },
    { label: 'Win Rate', value: `${accuracy}%`, color: accuracy >= 50 ? 'var(--green)' : scored.length === 0 ? 'var(--text)' : 'var(--red)' },
    { label: 'Avg Move ✓', value: `${avgMoveCorrect}%`, color: 'var(--green)' },
    { label: 'Avg Move ✗', value: `${avgMoveIncorrect}%`, color: 'var(--red)' },
    { label: 'Hypo Return', value: fmtPnl(hypoReturn), color: hypoReturn >= 0 ? 'var(--green)' : scored.length === 0 ? 'var(--text)' : 'var(--red)' },
    { label: 'Pending', value: pending.length, color: 'var(--text2)' },
  ]

  return (
    <>
    <div className="animate-in">
      <style>{`
        @media (max-width: 600px) {
          .mystats-title { font-size: 26px !important; }
          .mystats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
          .mystats-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .mystats-table { min-width: 480px; }
          .mystats-table td, .mystats-table th { padding: 8px 10px !important; }
        }
      `}</style>
      <div style={{ marginBottom: 28 }}>
        <h1 className="mystats-title" style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>
          My Stats
        </h1>
        <p style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
          Your prediction track record
        </p>
      </div>

      {!user && (
        <div style={{
          background: 'var(--olive-bg)', border: '1px solid var(--olive-dim)',
          borderRadius: 6, padding: '16px 20px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 3, fontSize: 14 }}>Sign in to save permanently</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>
              {predictions.length} local prediction{predictions.length !== 1 ? 's' : ''} — create a free account to keep them.
            </div>
          </div>
          <button onClick={() => setShowAuth(true)} style={{
            background: 'var(--olive)', border: 'none', color: '#fff',
            padding: '8px 18px', borderRadius: 4, fontWeight: 600, fontSize: 13,
            cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
          }}>Sign up free</button>
        </div>
      )}

      {/* Summary box — always shown */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '24px', marginBottom: 24,
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 20 }}>
          Summary
        </div>
        <div className="mystats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {statItems.map(s => (
            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                {s.label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--mono)', color: s.color }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {scored.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ height: 5, borderRadius: 3, background: 'var(--surface3)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3, width: `${accuracy}%`,
                background: accuracy >= 60 ? 'var(--green)' : accuracy >= 40 ? 'var(--olive)' : 'var(--red)',
                transition: 'width 0.8s ease',
              }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 6 }}>
              {correct} correct · {incorrect} incorrect · {pending.length} pending
            </div>
          </div>
        )}
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
            Pending · {pending.length}
          </div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {[...pending].sort((a, b) => new Date(a.report_date) - new Date(b.report_date)).map((p, i) => (
              <div key={p.id || i} style={{
                padding: '12px 20px', borderBottom: i < pending.length - 1 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontWeight: 500, color: 'var(--olive)', fontSize: 13, minWidth: 60 }}>{p.ticker}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{p.report_date}</span>
                  <span style={{
                    fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 500,
                    color: p.direction === 'up' ? 'var(--green)' : 'var(--red)',
                    background: p.direction === 'up' ? 'var(--green-bg)' : 'var(--red-bg)',
                    padding: '2px 8px', borderRadius: 3,
                  }}>{p.direction === 'up' ? '↑ UP' : '↓ DOWN'}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--surface3)', padding: '2px 8px', borderRadius: 2, fontFamily: 'var(--mono)' }}>
                  PENDING
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scored table */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
          Scored · {scored.length}
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {scored.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' }}>
              No scored predictions yet — check back after earnings are reported
            </div>
          ) : (
            <div className="mystats-table-wrap"><table className="mystats-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'date', label: 'Date' },
                    { key: null, label: 'Ticker' },
                    { key: null, label: 'Your Pick' },
                    { key: 'move', label: 'Actual Move' },
                    { key: null, label: 'Result' },
                    { key: 'pnl', label: 'P&L ($10k)' },
                  ].map((col, i) => (
                    <th key={i} onClick={() => col.key && handleSort(col.key)} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)',
                      cursor: col.key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
                    }}>
                      {col.label}{col.key && <SortArrow col={col.key} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedScored.map((p, i) => {
                  const pnl = p.direction_correct
                    ? 10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100
                    : -10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100
                  return (
                    <tr key={p.id || i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
                      <td style={{ padding: '11px 16px', fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{p.report_date}</td>
                      <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 13, color: 'var(--olive)' }}>{p.ticker}</td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{
                          fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 500,
                          color: p.direction === 'up' ? 'var(--green)' : 'var(--red)',
                          background: p.direction === 'up' ? 'var(--green-bg)' : 'var(--red-bg)',
                          padding: '2px 8px', borderRadius: 3,
                        }}>{p.direction === 'up' ? '↑ UP' : '↓ DOWN'}</span>
                      </td>
                      <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 500 }}>
                        <span style={{ color: (parseFloat(p.actual_change) || 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                          {(parseFloat(p.actual_change) || 0) >= 0 ? '+' : ''}{(parseFloat(p.actual_change) || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{
                          fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 600,
                          color: p.direction_correct ? 'var(--green)' : 'var(--red)',
                          background: p.direction_correct ? 'var(--green-bg)' : 'var(--red-bg)',
                          padding: '2px 8px', borderRadius: 2,
                        }}>{p.direction_correct ? '✓ CORRECT' : '✗ WRONG'}</span>
                      </td>
                      <td style={{ padding: '11px 16px', fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {pnl >= 0 ? '+' : '-'}${Math.abs(pnl).toFixed(0)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

    </div>
    {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  )
}
